import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { documentApi } from '@/entities/Document';
import { toastError, toastSuccess, useCustomToast } from '@/shared/lib';
import useFileUploadStore from './upload/useFileUploadStore';

export default function useReplaceDocument() {
  const toast = useCustomToast();
  const fileUploadStore = useFileUploadStore();
  const { filesLoaded: filesLoadedRef } = storeToRefs(fileUploadStore);

  const url = ref('');
  const urlError = ref('');
  const isSubmitting = ref(false);

  const isValidUrl = computed(() => {
    if (!url.value) return false;
    try {
      new URL(url.value);
      return true;
    } catch {
      return false;
    }
  });

  const handleUrlInput = () => {
    if (url.value && !isValidUrl.value) {
      urlError.value = 'Formato incorreto';
    } else {
      urlError.value = '';
    }
  };

  const handleInput = (e: Event) => {
    url.value = (e.target as HTMLInputElement).value;
  };

  watch(url, handleUrlInput);

  const canSave = (isUrlDocument: boolean) => {
    if (isUrlDocument) return isValidUrl.value && !isSubmitting.value;
    return filesLoadedRef.value && !isSubmitting.value;
  };

  const replaceDocument = async (
    documentId: number,
    revisionId: number,
    isUrlDocument: boolean,
    urlValue: string,
    onSuccess?: () => void
  ) => {
    isSubmitting.value = true;
    try {
      if (isUrlDocument) {
        await documentApi.replaceRevisionUrl(documentId, revisionId, urlValue);
      } else {
        await fileUploadStore.uploadSelectedFiles(undefined, {
          documentId,
          revisionId,
        });
      }
      toastSuccess(toast, 'Documento substituído', 'Documento substituído com sucesso.');
      onSuccess?.();
    } catch {
      toastError(toast, 'Erro ao substituir', 'Erro ao enviar documento, tente novamente.');
    } finally {
      isSubmitting.value = false;
    }
  };

  const handleConfirm = async (
    documentId: number,
    revisionId: number,
    isUrlDocument: boolean,
    onClose: () => void
  ) => {
    if (!canSave(isUrlDocument)) return;
    await replaceDocument(documentId, revisionId, isUrlDocument, url.value, onClose);
  };

  return {
    url,
    urlError,
    isSubmitting,
    isValidUrl,
    canSave,
    handleInput,
    handleUrlInput,
    replaceDocument,
    handleConfirm,
  };
}
